import { useMemo } from 'react';
import styled from 'styled-components';
import { List, Column } from '@elementary/common-ui/components/Table';
import { numberWithCommas } from '@elementary/common-ui/utils/common';
import UserCard from '../shared/UserCard';
import { LeaderboardRow } from './types';

const ListContainer = styled.div<{ $hideRowBorders: boolean }>`
  ${({ $hideRowBorders }) =>
    $hideRowBorders &&
    `[role='cell'].MuiTableCell-root {
      border-bottom: none;
    }`}
`;

type Row = LeaderboardRow & { rank: number };

type Props = {
  entries: LeaderboardRow[];
};

const UsageLeaderboard = ({ entries }: Props) => {
  const data: Row[] = useMemo(
    () => entries.map((entry, index) => ({ ...entry, rank: index + 1 })),
    [entries],
  );

  const columns: Array<Column<Row>> = useMemo(
    () => [
      {
        id: 'rank',
        header: '',
        width: 48,
        filterable: false,
        sortable: false,
        renderer: ({ rank }) => <>{rank}</>,
      },
      {
        id: 'user',
        header: 'User',
        width: '100%',
        filterable: false,
        showValueWhenEmpty: true,
        renderer: ({ userId, name, email }) => (
          <UserCard
            id={userId}
            name={name ?? email ?? userId}
            email={email ?? ''}
            sx={{ gap: 1.5 }}
          />
        ),
        compareFunc: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      },
      {
        id: 'credits',
        header: 'Credits',
        width: 120,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: ({ credits }) => <>{numberWithCommas(Math.round(credits))}</>,
        compareFunc: (a, b) => a.credits - b.credits,
      },
      {
        id: 'aiRuns',
        header: 'Sessions',
        width: 120,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: ({ aiRuns }) => <>{numberWithCommas(aiRuns)}</>,
        compareFunc: (a, b) => a.aiRuns - b.aiRuns,
      },
    ],
    [],
  );

  return (
    <ListContainer $hideRowBorders={data.length <= 1}>
      <List
        columns={columns}
        data={data}
        defaultSortKey="aiRuns"
        defaultSortDirection="desc"
      />
    </ListContainer>
  );
};

export default UsageLeaderboard;
